import CrownIcon from 'assets/Icons/CrownIcon';
import VerifiedIcon from 'assets/Icons/verifiedIcon';
import VerifyBadgeIcon from 'assets/Icons/VerifyBadgeIcon';
import clsx from 'clsx';
import moment from 'moment';
import { useRouter } from 'next/router';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { montserrat } from '@/components/Layout';
import { RenderAvatarProps } from '@/components/Layout/ProductsHeader';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { user } from '@/atoms/user';
import { isPlatformSubscriptionActive } from '@/utils/index';
import { User } from '@/utils/interfaces/User';

interface Props {
  RenderAvatar: ({
    containerStyle,
    wrapperStyle,
    width,
    redirect,
    avatarStyle,
    userName,
    avatarSrc,
    gradientBg,
    setShowDropDown,
    onClose,
  }: RenderAvatarProps) => React.JSX.Element;
  userName: string | undefined;
  avatarSrc: string | null | undefined;
  gradientBg: string;
  setShowDropDown: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  displayName: string;
  className?: string;
}

const ViewProfile: React.FC<Props> = ({
  onClose,
  RenderAvatar,
  userName,
  avatarSrc,
  gradientBg,
  setShowDropDown,
  setOpen,
  displayName,
  className,
}) => {
  const router = useRouter();

  const userData = useRecoilValue<User | undefined>(user);

  return (
    <div
      className={twMerge(
        `relative flex justify-start gap-x-3 items-center w-full px-5   
                ${isPlatformSubscriptionActive(userData) ? ' bg-[linear-gradient(152deg,_rgba(89,58,26,0.1)_9.93%,_rgba(211,169,132,0.1)_90.07%)] pb-4 pt-4 ' : 'pt-6 lg:pt-6'}
                `,
        className,
      )}
    >
      <ShouldRender check={isPlatformSubscriptionActive(userData)}>
        <div className='w-full h-px bg-[linear-gradient(152deg,_#593A1A,_#D3A984)] absolute bottom-0 left-0 z-40' />
      </ShouldRender>
      <div
        className={clsx(
          montserrat.className,
          'min-h-[48px] flex justify-start  w-min',
        )}
      >
        <RenderAvatar
          wrapperStyle='rounded-full  min-w-[25px] min-h-[25px] flex justify-center items-center lg:min-w-[48px] lg:min-h-[48px]'
          width={48}
          redirect={true}
          avatarStyle={{
            fontSize: '14px',
            border: isPlatformSubscriptionActive(userData)
              ? '2px solid #D3A984'
              : '1px solid #FFFFFF0D',
          }}
          userName={userName}
          avatarSrc={avatarSrc as string}
          gradientBg={gradientBg}
          setShowDropDown={setShowDropDown}
          onClose={onClose}
        />
      </div>
      <div className='flex flex-col gap-0.5 items-start'>
        {isPlatformSubscriptionActive(userData) ? (
          <button
            onClick={() => {
              setShowDropDown(false);

              router.push('/plans');
              setOpen(false);
            }}
            className='flex items-center justify-center mb-2 bg-gradient-to-br from-[#593A1A] to-[#D3A984] rounded-lg '
          >
            <div
              className='flex items-center justify-center gap-2 py-2 px-3 m-px rounded-lg'
              style={{
                background:
                  'linear-gradient(152deg, #1B1815 9.93%, #1E1A17 90.07%)',
              }}
            >
              <CrownIcon className='w-4 h-4' />
              <p className='text-xs/4 text-[#D3A984] font-medium'>
                {/* {`Subscription Renews in ${moment(userData?.platform_subscription?.expired_at).diff(moment(), 'days')} Days`} */}
                {(() => {
                  const daysLeft = moment(
                    userData?.platform_subscription?.expired_at,
                  ).diff(moment(), 'days');

                  return daysLeft <= 0
                    ? 'Subscription Expired'
                    : `Subscription Ends in ${daysLeft} Days`;
                })()}
              </p>
            </div>
          </button>
        ) : (
          <></>
        )}

        <div className='flex gap-x-1.5 items-center'>
          <span
            onClick={() => {
              setShowDropDown(false);
              setOpen(false);
              router.push('/profile');
            }}
            className='font-semibold text-lg leading-[-0.09px] capitalize cursor-pointer truncate'
          >
            {displayName}
          </span>
          {/* <ShouldRender check={!userData?.email_verified_at}>
                      <Button
                        size='small'
                        type='text'
                        onClick={() => {
                          sendVerificationLink();
                          setShowDropDown(false);
                          onClose();
                        }}
                        style={{
                          position: 'relative',
                          overflow: 'hidden',
                          background:
                            'linear-gradient(180deg, rgba(97, 153, 255, 0.1) 0%, rgba(4, 92, 251, 0.1) 100%)',
                          border: 'none',
                          borderRadius: '20px',
                          padding: '5px 15px',
                          zIndex: 1,
                        }}
                        className='h-[24px] hover:text-white text-[10px] font-semibold'
                      >
                        <span
                          style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: -1,
                            borderRadius: 'inherit',
                            padding: '1.3px',
                            background:
                              'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                            WebkitMask:
                              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                          }}
                        />
                        Get Verified
                      </Button>
                    </ShouldRender> */}
          <ShouldRender check={userData?.email_verified_at}>
            <VerifiedIcon />
          </ShouldRender>
          <ShouldRender check={!userData?.email_verified_at}>
            <VerifyBadgeIcon fill='#504f53' width={20} height={20} />
          </ShouldRender>
        </div>
        <p
          onClick={() => {
            setShowDropDown(false);
            setOpen(false);
            router.push('/profile');
          }}
          className='text-xs/5 font-normal text-grayZl-500'
        >
          View Profile{' '}
        </p>
      </div>
    </div>
  );
};

export default ViewProfile;
